import {
    BillingLlmBlockedError,
    type BillingThresholdState,
    type BillingBlockedPayload,
} from "~/features/billing/types";

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

function toNullableNumber(value: unknown): number | null {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function toNullableBoolean(value: unknown): boolean | null {
    return typeof value === "boolean" ? value : null;
}

function toNullableString(value: unknown): string | null {
    return typeof value === "string" ? value : null;
}

function toThresholdState(value: unknown): BillingThresholdState | null {
    return value === "active" ||
        value === "warning_80" ||
        value === "warning_100" ||
        value === "blocked"
        ? value
        : null;
}

export function parseBillingBlockedResponseBody(
    body: unknown,
): BillingBlockedPayload | null {
    if (!isRecord(body)) return null;
    if (body.code !== "BILLING_LLM_BLOCKED") return null;

    const billingValue = body.billing;
    if (!isRecord(billingValue)) return null;

    const thresholdState = toThresholdState(billingValue.thresholdState);
    if (!thresholdState) return null;

    const enforced = toNullableBoolean(billingValue.enforced);
    if (enforced === null) return null;

    return {
        code: "BILLING_LLM_BLOCKED",
        message:
            toNullableString(body.error) ||
            "Session processing is paused while your plan's session limit is reached.",
        enforced,
        metricType:
            billingValue.metricType === "session" ||
            billingValue.metricType === "client"
                ? billingValue.metricType
                : null,
        thresholdState,
        blockReason:
            billingValue.blockReason === "trial_hard_stop" ||
            billingValue.blockReason === "free_tier_hard_stop" ||
            billingValue.blockReason === "paid_plan_over_grace"
                ? billingValue.blockReason
                : null,
        usedUnits: toNullableNumber(billingValue.usedUnits),
        limitUnits: toNullableNumber(billingValue.limitUnits),
    };
}

export async function parseBillingBlockedResponse(
    response: Response,
): Promise<BillingBlockedPayload | null> {
    const body = await response.json().catch(() => null);
    return parseBillingBlockedResponseBody(body);
}

export function getErrorMessageFromResponseBody(
    body: unknown,
    fallback: string,
): string {
    if (isRecord(body) && typeof body.error === "string") {
        return body.error;
    }
    return fallback;
}

// AIDEV-NOTE: Centralized creator ensures all UI surfaces receive the same structured billing error.
export function createBillingLlmBlockedError(
    payload: BillingBlockedPayload,
): BillingLlmBlockedError {
    return new BillingLlmBlockedError(payload);
}
